import { useEffect, useState } from 'react';
import Api from '../Auth/Api';
import { decodeJWT } from '../Components/jwtUtils';
import { Chart as ChartJS, Tooltip, Legend, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler } from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

// Componentes
import ActionButtons from '../Components/Dashboard/ActionButtons';
import CourseSelector from '../Components/Dashboard/CourseSelector';
import SidebarContent from '../Components/Dashboard/SidebarContent';
import UsuariosManager from '../Components/Dashboard/Usuarios/UsuariosManager';
import CursosManager from '../Components/Dashboard/Cursos/CursosManager';
import SistemaInfo from '../Components/Dashboard/SistemaInfo';
import { useAjustes } from '../Context/AjustesContext';

ChartJS.register(Tooltip, Legend, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Filler);

const MONTH_LABELS = { '01':'Jan','02':'Fev','03':'Mar','04':'Abr','05':'Mai','06':'Jun','07':'Jul','08':'Ago','09':'Set','10':'Out','11':'Nov','12':'Dez' };

function Dashboard() {
    const token = JSON.parse(localStorage.getItem("user_token"))
    const nivel = decodeJWT(token).nivel;

    const { ajustes } = useAjustes();
    const corPrimaria = ajustes?.cor_primaria || '#3a5a6c';

    const isMobile = window.matchMedia('(max-width: 767px)').matches;

    const [date, setDate] = useState(new Date());
    const [idCursoInicio, setIdCursoInicio] = useState(null);
    const [dataModulosInicio, setDataModulosInicio] = useState([]);
    const [loadingInfo, setLoadingInfo] = useState(false);

    const [infoSistema, setInfoSistema] = useState({
        total_alunos: 0,
        total_usuarios: 0,
        total_cursos: 0,
        total_certificados: 0,
        total_aulas_concluidas: 0,
        cursos_mais_vistos: [],
        top_cursos: [],
        certificados_por_mes: [],
    });

    // Inicialização dos gerenciadores
    const usuariosManager = UsuariosManager({ token, nivel });
    const cursosManager = CursosManager({ token, nivel });

    // Efeito para buscar as informações do sistema
    useEffect(() => {
        async function fetchSistemaInfo() {
            if (nivel === 'Administrador' || nivel === 'Gestor' || nivel === 'RH') {
                setLoadingInfo(true);
                try {
                    const response = await Api.get(`api/sistema/info`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setInfoSistema(response.data.data);
                } catch (error) {
                    console.error("Erro ao buscar informações do sistema:", error);
                } finally {
                    setLoadingInfo(false);
                }
            }
        }
        fetchSistemaInfo();
    }, [token, nivel]);

    // Efeito para buscar módulos quando um curso é selecionado
    useEffect(() => {
        if (idCursoInicio) {
            async function fetchModulos() {
                try {
                    const response = await Api.get(`api/listar-modulos/${idCursoInicio}`, {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                    setDataModulosInicio(response.data);
                } catch (error) {
                    console.error(error.response?.data?.message || error);
                    setDataModulosInicio([]);
                }
            }
            fetchModulos();
        } else {
            setDataModulosInicio([]);
        }
    }, [token, idCursoInicio]);

    const [dataAulasAdicionadas, setDataAulasAdicionadas] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`api/ultimas-aulas`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataAulasAdicionadas(response.data);
            } catch (error) {
                console.error(error);
            }
        }
        fetchData();
    }, [token]);

    // Handler para alterar curso selecionado
    const handleCursoChange = (selectedOption) => {
        if (selectedOption) {
            setIdCursoInicio(selectedOption.value);
        } else {
            setIdCursoInicio(null);
        }
    };

    const isGestorOuAdmin = nivel === 'Administrador' || nivel === 'Gestor' || nivel === 'RH';

    const cursosMaisVistosData = infoSistema.cursos_mais_vistos?.length > 0 ? {
        labels: infoSistema.cursos_mais_vistos.map(c => c.nome.length > 22 ? c.nome.slice(0, 22) + '…' : c.nome),
        datasets: [{
            label: 'Visualizações',
            data: infoSistema.cursos_mais_vistos.map(c => Number(c.total_visualizacoes)),
            backgroundColor: 'rgba(139,92,246,0.7)',
            borderColor: '#8b5cf6',
            borderWidth: 2,
            borderRadius: 6,
        }]
    } : null;

    const topCursosData = infoSistema.top_cursos?.length > 0 ? {
        labels: infoSistema.top_cursos.map(c => c.nome.length > 25 ? c.nome.slice(0, 25) + '…' : c.nome),
        datasets: [{
            label: 'Alunos',
            data: infoSistema.top_cursos.map(c => Number(c.total_alunos)),
            backgroundColor: `${corPrimaria}b3`,
            borderColor: corPrimaria,
            borderWidth: 2,
            borderRadius: 6,
        }]
    } : null;

    const certsMesData = infoSistema.certificados_por_mes?.length > 0 ? (() => {
        const labels = infoSistema.certificados_por_mes.map(m => {
            const [, mes] = m.mes.split('-');
            return MONTH_LABELS[mes] || m.mes;
        });
        return {
            labels,
            datasets: [{
                label: 'Certificados',
                data: infoSistema.certificados_por_mes.map(m => Number(m.total)),
                backgroundColor: 'rgba(16,185,129,0.15)',
                borderColor: '#10b981',
                borderWidth: 2,
                fill: true,
                tension: 0.4,
                pointBackgroundColor: '#10b981',
            }]
        };
    })() : null;

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
    };

    const ChartPlaceholder = ({ height = 220, text }) => (
        <div className="d-flex justify-content-center align-items-center text-muted" style={{ height }}>
            {loadingInfo
                ? <div className="spinner-border text-primary" role="status"></div>
                : <span>{text}</span>
            }
        </div>
    );

    return (

        <div className="row g-3 g-md-4 mb-6">
            <div className="col-12 col-xl-8 col-xxl-9">

                {isGestorOuAdmin && (
                    <SistemaInfo infoSistema={infoSistema} />
                )}

                <ActionButtons
                    nivel={nivel}
                    usuariosManager={usuariosManager}
                />
                {!isMobile && (
                    <CourseSelector
                        dataCursos={cursosManager.dataCursos}
                        dataModulosInicio={dataModulosInicio}
                        handleCursoChange={handleCursoChange}
                        customStyles={cursosManager.customStyles}
                    />
                )}

                {/* Gráficos — visível apenas para admin/gestor/RH */}
                {isGestorOuAdmin && (
                    <>
                        {/* Linha 1: Cursos mais vistos + Top cursos */}
                        <div className="row g-3 mt-1">
                            <div className="col-md-4">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body p-4">
                                        <h6 className="fw-semibold text-primary mb-3">
                                            <i className="bi bi-eye me-2"></i>Cursos mais vistos
                                        </h6>
                                        {cursosMaisVistosData
                                            ? <div style={{ height: 220 }}><Bar data={cursosMaisVistosData} options={{ ...chartOptions, indexAxis: 'y', plugins: { legend: { display: false } } }} /></div>
                                            : <ChartPlaceholder height={220} text="Nenhum acesso registrado" />
                                        }
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-8">
                                <div className="card border-0 shadow-sm h-100">
                                    <div className="card-body p-4">
                                        <h6 className="fw-semibold text-primary mb-3">
                                            <i className="bi bi-bar-chart me-2"></i>Top 5 cursos com mais alunos
                                        </h6>
                                        {topCursosData
                                            ? <div style={{ height: 220 }}><Bar data={topCursosData} options={chartOptions} /></div>
                                            : <ChartPlaceholder height={220} text="Nenhum curso cadastrado" />
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Linha 2: Line chart */}
                        <div className="row g-3 mt-1">
                            <div className="col-12">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-body p-4">
                                        <h6 className="fw-semibold text-primary mb-3">
                                            <i className="bi bi-graph-up me-2"></i>Certificados emitidos (últimos 12 meses)
                                        </h6>
                                        {certsMesData
                                            ? <div style={{ height: 220 }}><Line data={certsMesData} options={{ ...chartOptions, plugins: { legend: { display: false } } }} /></div>
                                            : <ChartPlaceholder height={220} text="Nenhum certificado emitido nos últimos 12 meses" />
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

            </div>

            <div className="col-12 col-xl-4 col-xxl-3">
                <SidebarContent
                    date={date}
                    setDate={setDate}
                    formatShortWeekday={(locale, date) => date.toLocaleString(locale, { weekday: 'short' }).slice(0, 3)}
                    nivel={nivel}
                    dataAulasAdicionadas={dataAulasAdicionadas}
                />
            </div>

            {/* Renderiza o modal de usuário */}
            {usuariosManager.renderModal(cursosManager.dataCursos)}
        </div>

    );
}

export default Dashboard;